import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/vdiTransaction.slice'; // Import your action creators
import * as api from '@/services/vdiTransaction'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import {
  IDeleteVdiTransaction,
  IFetchVdiTransaction,
} from '@/services/vdiTransaction/vdiTransaction.type';
import { IPagination } from '../types';

// Define action types
export interface GetVdiTransactionAction extends PayloadAction<number> {
  type: 'vditransaction/getVditransaction';
}

export interface GetVdiTransactionrsAction extends PayloadAction<IFetchVdiTransaction> {
  type: 'vditransaction/getVditransactions';
}

export interface CreateVdiTransactionAction extends PayloadAction<IVDITransactionProps> {
  type: 'vditransaction/createVditransaction';
}

export interface UpdateVdiTransactionAction extends PayloadAction<IVDITransactionProps> {
  type: 'vditransaction/updateVditransaction';
}

export interface DeleteVdiTransactionAction extends PayloadAction<IDeleteVdiTransaction> {
  type: 'vditransaction/deleteVditransaction';
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data: IVDITransactionProps[];
  pagination: IPagination;
}

export interface ISingleVdiResponse {
  success: boolean;
  message: string;
  data: IVDITransactionProps[];
  pagination: IPagination;
}

function* getVditransactions(action: GetVdiTransactionrsAction): Generator {
  try {
    const response: any = yield call(api.fetchVdiTransactions, action?.payload);
    if (isValidVditransactionsResponse(response?.data)) {
      yield put(actions.getVditransactionsSuccess(response?.data));
    } else {
      yield put(actions.getVditransactionsFailure());
    }
  } catch (error) {
    yield put(actions.getVditransactionsFailure());
  }
}

// Your saga function
function* getVditransaction(action: GetVdiTransactionAction): Generator {
  try {
    const response: any = yield call(api.fetchVdiTransaction as any, action?.payload);
    const res = response?.data?.data;

    if (isValidVditransactionResponse(response.data)) {
      yield put(actions.getVditransactionSuccess(res));
    } else {
      yield put({ type: 'vditransaction/getVditransactionFailure' });
    }
  } catch (error) {
    yield put({ type: 'vditransaction/getVditransactionFailure' });
  }
}

function* createVditransaction(action: CreateVdiTransactionAction) {
  try {
    const response: unknown = yield call(
      api.createVdiTransaction as any,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'vditransaction/createVditransactionSuccess',
        payload: response as ISingleVdiResponse,
      });
    } else {
      yield put({ type: 'vditransaction/createVditransactionFailure' });
    }
  } catch (error) {
    yield put({ type: 'vditransaction/createVditransactionFailure' });
  }
}

function* updateVditransaction(action: UpdateVdiTransactionAction) {
  try {
    const response: unknown = yield call(
      api.updateVdiTransaction as any,
      action?.payload?.providerId,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'vditransaction/updateProviderSuccess',
        payload: response as ISingleVdiResponse,
      });
    } else {
      yield put({
        type: 'vditransaction/updateProviderFailure',
        payload: response,
      });
    }
  } catch (error) {
    yield put({
      type: 'vditransaction/updateProviderFailure',
      payload: 'Something went wrong',
    });
  }
}

function* deleteVditransaction(action: DeleteVdiTransactionAction) {
  try {
    const response: unknown = yield call(
      api.deleteVdiTransaction as any,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'vditransaction/deleteProviderSuccess',
        payload: response as ISingleVdiResponse,
      });
    } else {
      yield put({ type: 'vditransaction/deleteProviderFailure' });
    }
  } catch (error) {
    yield put({
      type: 'vditransaction/deleteProviderFailure',
      payload: 'Something went wrong',
    });
  }
}

function isValidVditransactionResponse(response: ApiResponse): boolean {
  return (
    response.success === true &&
    response.message === 'Successfully got VDITransaction.'
  );
}

function isValidProviderResponse(response: unknown): response is ISingleVdiResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response
  );
}

function isValidVditransactionsResponse(response: ApiResponse): boolean {
  return (
    response.success === true &&
    response.message === 'Successfully got list of VDITransactions.'
  );
}

function* providerSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetVdiTransactionrsAction>(
    'vditransaction/getVditransactions',
    getVditransactions,
  );
  yield takeEvery<GetVdiTransactionAction>(
    'vditransaction/getVditransaction',
    getVditransaction,
  );
  yield takeEvery<CreateVdiTransactionAction>(
    'vditransaction/createVditransaction',
    createVditransaction,
  );
  yield takeEvery<UpdateVdiTransactionAction>(
    'vditransaction/updateVditransaction',
    updateVditransaction,
  );
  yield takeEvery<DeleteVdiTransactionAction>(
    'vditransaction/deleteVditransaction',
    deleteVditransaction,
  );
}

export default providerSaga;
